import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, forkJoin, map, mergeMap, Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';

interface IWorkItemReference {
    id: string,
    url: string
}

interface ICommentBody {
    text: string
}

@Injectable({
    providedIn: 'root'
})
export class AdoRepositoryService {
    orgUrl: string = 'https://dev.azure.com/wheelme';
    projectName: string = environment.adoApiConfig.project;
    apiVersion: string = environment.adoApiConfig.apiVersion;
    projectApiUrl: string = `${this.orgUrl}/${this.projectName}/_apis`
    jsonHeader: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json');
    pachHeader: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json-patch+json');



    constructor(
        private http: HttpClient,
        private authService: MsalService
    ) {
    }

    post<T>(
        apiEndpoint: string,
        data: T,
        options?: { httpHeaders?: HttpHeaders; versionFlag?: string }
    ): Observable<HttpResponse<any>> {
        const headers = options?.httpHeaders ?? this.jsonHeader;
        return this.http.post(this._getAdoApiUrl(apiEndpoint, options?.versionFlag), data, {
            headers: headers,
            observe: "response",
        });
    }

    get<T>(
        apiEndpoint: string,
        options?: { httpHeaders?: HttpHeaders; query?: any, versionFlag?: string }
    ): Observable<HttpResponse<any>> {
        const params = options?.query ? new HttpParams({ fromObject: options.query }) : undefined;
        const headers = options?.httpHeaders ?? this.jsonHeader;
        return this.http.get(this._getAdoApiUrl(apiEndpoint, options?.versionFlag), {
            headers: headers,
            observe: "response",
            params: params
        });
    }

    patch<T>(
        apiEndpoint: string,
        data: T,
        options?: { httpHeaders?: HttpHeaders; versionFlag?: string }
    ): Observable<HttpResponse<any>> {
        const headers = options?.httpHeaders ?? this.pachHeader;
        const queryResponse = this.http.patch(
            this._getAdoApiUrl(apiEndpoint, options?.versionFlag),
            data,
            {
                headers: headers,
                observe: "response",
            },
        );
        return queryResponse;
    }

    getWorkItemsByWiql(query: string): Observable<IWorkItemReference[]> {
        return this.post("wit/wiql", { query: query }).pipe(
            map(res => (res.body.workItems as IWorkItemReference[])),
        );
    }


    getAllActiveBugs(): Observable<any> {
        const wiqlQuery: string = `
            SELECT [System.Id], [System.Title]
            FROM WorkItems
            WHERE [System.WorkItemType] = 'Issue'
            AND [System.TeamProject] = '${environment.adoApiConfig.project}'
        `;

        return this.getWorkItemsByWiql(wiqlQuery).pipe(
            mergeMap(items =>
                forkJoin(
                    items.map((item: IWorkItemReference) =>
                        this.get(`wit/workitems/${item.id}`).pipe(
                            map(wi => wi.body)
                        )
                    )
                )
            )
        );
    }

    getTitleFromTicketId(ticketId: number | string): Observable<string> {
        return this.get(
            `wit/workitems/${ticketId}`,
            { query: { fields: "System.Title" } }
        ).pipe(
            map(res => {
                return res.body.fields["System.Title"] ?? "Not able to get title";
            }),
            catchError((err: any) => {
                return of("Not able to get title");
            })
        );
    }

    postComment(itemId: number | string, comment: string): Observable<any> {
        const commentBody: ICommentBody = this._getCommentAsRequestBody(comment);
        return this.post(this._getCommentEndpoint(itemId), commentBody, { versionFlag: "preview.3" });
    }

    _getCommentEndpoint(itemId: number | string) {
        return `/wit/workItems/${itemId}/comments`
    }

    _getCommentAsRequestBody(comment: string): ICommentBody {
        return { text: comment }
    }

    _getAdoApiUrl(apiEndpoint: string, versionFlag?: string) {
        const version = versionFlag ? `${this.apiVersion}-${versionFlag}` : this.apiVersion;
        return `${this.projectApiUrl}/${apiEndpoint.trim()}?api-version=${version}`
    }

}
